const ACCESS_TOKEN_KEY = 'admin_access_token';
const REFRESH_TOKEN_KEY = 'admin_refresh_token';
const REDIRECT_PATH_KEY = 'admin_post_login_redirect_path';
const SESSION_TIMEOUT_KEY = 'admin_session_timeout_flag';
const LAST_INTERACTION_KEY = 'admin_last_interaction_at';
const LAST_REFRESH_KEY = 'admin_last_refresh_at';
const WARNING_DEADLINE_KEY = 'admin_session_warning_deadline_at';
const EXTENDING_SESSION_KEY = 'admin_session_extending_in_progress';

export const ADMIN_IDLE_TIMEOUT_MS = 5 * 60 * 1000;
export const ADMIN_WARNING_WINDOW_MS = 60 * 1000;
export const ADMIN_REFRESH_INTERVAL_MS = 4 * 60 * 1000;

export const getAdminAccessToken = () => sessionStorage.getItem(ACCESS_TOKEN_KEY);
export const getAdminRefreshToken = () => sessionStorage.getItem(REFRESH_TOKEN_KEY);
export const getAdminLastInteractionAt = () => Number(sessionStorage.getItem(LAST_INTERACTION_KEY) || 0);
export const getAdminLastRefreshAt = () => Number(sessionStorage.getItem(LAST_REFRESH_KEY) || 0);

export const setAdminSessionTokens = ({ accessToken, refreshToken }) => {
  if (accessToken) sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  const now = Date.now();
  if (!getAdminLastInteractionAt()) {
    sessionStorage.setItem(LAST_INTERACTION_KEY, String(now));
  }
  sessionStorage.setItem(LAST_REFRESH_KEY, String(now));
};

export const clearAdminSessionTokens = () => {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(LAST_INTERACTION_KEY);
  sessionStorage.removeItem(LAST_REFRESH_KEY);
  sessionStorage.removeItem(WARNING_DEADLINE_KEY);
  sessionStorage.removeItem(EXTENDING_SESSION_KEY);
};

export const setAdminRedirectPath = (path) => {
  if (!path) return;
  sessionStorage.setItem(REDIRECT_PATH_KEY, path);
};

export const consumeAdminRedirectPath = () => {
  const value = sessionStorage.getItem(REDIRECT_PATH_KEY);
  sessionStorage.removeItem(REDIRECT_PATH_KEY);
  return value;
};

export const markAdminSessionTimedOut = () => {
  sessionStorage.setItem(SESSION_TIMEOUT_KEY, '1');
};

export const consumeAdminSessionTimedOut = () => {
  const value = sessionStorage.getItem(SESSION_TIMEOUT_KEY) === '1';
  sessionStorage.removeItem(SESSION_TIMEOUT_KEY);
  return value;
};

export const markAdminInteraction = (timestamp = Date.now()) => {
  sessionStorage.setItem(LAST_INTERACTION_KEY, String(timestamp));
};

export const markAdminSessionRefreshed = (timestamp = Date.now()) => {
  sessionStorage.setItem(LAST_REFRESH_KEY, String(timestamp));
};

export const setAdminWarningDeadline = (timestamp) => {
  if (!timestamp) return;
  sessionStorage.setItem(WARNING_DEADLINE_KEY, String(timestamp));
};

export const getAdminWarningDeadline = () => Number(sessionStorage.getItem(WARNING_DEADLINE_KEY) || 0);

export const clearAdminWarningDeadline = () => {
  sessionStorage.removeItem(WARNING_DEADLINE_KEY);
};

export const setAdminSessionExtending = (value) => {
  if (value) {
    sessionStorage.setItem(EXTENDING_SESSION_KEY, '1');
    return;
  }
  sessionStorage.removeItem(EXTENDING_SESSION_KEY);
};

export const isAdminSessionExtending = () => {
  return sessionStorage.getItem(EXTENDING_SESSION_KEY) === '1';
};

export const hasAdminSessionTimedOutByInactivity = (timestamp = Date.now()) => {
  const lastInteractionAt = getAdminLastInteractionAt();
  return Boolean(lastInteractionAt) && timestamp - lastInteractionAt > ADMIN_IDLE_TIMEOUT_MS;
};

export const isAdminSessionRefreshDue = (timestamp = Date.now()) => {
  const lastRefreshAt = getAdminLastRefreshAt();
  return !lastRefreshAt || timestamp - lastRefreshAt >= ADMIN_REFRESH_INTERVAL_MS;
};

export const getAdminIdleRemainingMs = (timestamp = Date.now()) => {
  const lastInteractionAt = getAdminLastInteractionAt();
  if (!lastInteractionAt) return ADMIN_IDLE_TIMEOUT_MS;
  return Math.max(0, ADMIN_IDLE_TIMEOUT_MS - (timestamp - lastInteractionAt));
};

export const shouldWarnAdminSessionExpiry = (timestamp = Date.now()) => {
  const remaining = getAdminIdleRemainingMs(timestamp);
  return remaining > 0 && remaining <= ADMIN_WARNING_WINDOW_MS;
};

export const isAdminWarningGraceActive = (timestamp = Date.now()) => {
  const deadline = getAdminWarningDeadline();
  return Boolean(deadline) && timestamp < deadline;
};
